import React, { useMemo, useCallback } from 'react';
import EcoSection from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/eco-section';
import useSanityPermissionsChecker from 'helpers/hooks/useSanityPermissionsChecker';

interface Props {
  data: any;
  customerGroup: any;
}

const EcoSectionTastic: React.FC<Props> = ({ data, customerGroup }) => {
  const { checkNestedItemPermission } = useSanityPermissionsChecker();

  // Get cmsData from data (Sanity)
  const fields = data?.cmsData?.dataSource?.fields;

  // Helper: Get image URL from Sanity structure
  const getImageUrl = useCallback((image: any) => image?.fields?.file?.url || image?.url || image, []);

  // Helper: Check if item has permission
  const hasPermission = useCallback(
    (item: any) => checkNestedItemPermission(item, customerGroup),
    [checkNestedItemPermission, customerGroup],
  );

  // Helper: Filter items by permissions
  const filterByPermissions = useCallback((items: any[]) => items?.filter(hasPermission) || [], [hasPermission]);

  // Helper: Process tile data
  const processTile = useCallback(
    (tile: any) => {
      if (!hasPermission(tile)) {
        return { title: '', subtitle: '' };
      }
      const image = tile?.image;
      const imageUrl = getImageUrl(image);
      return {
        image: imageUrl ? ({ fields: { file: { url: imageUrl } } } as any) : undefined,
        title: tile?.title || '',
        subtitle: tile?.subtitle || '',
        ctaLabel: tile?.ctaLabel || '',
        ctaReference: tile?.ctaReference || '',
      };
    },
    [hasPermission, getImageUrl],
  );

  // Check top-level permissions
  const hasTopLevelPermission = useMemo(() => {
    if (fields?.userRoles) {
      return hasPermission({ userRoles: fields.userRoles });
    }
    return true;
  }, [fields?.userRoles, hasPermission]);

  // Process miniCards
  const miniCards = useMemo(() => {
    if (!hasTopLevelPermission) return [];

    return filterByPermissions(fields?.miniCards || []).map((miniCard: any) => {
      const imageUrl = getImageUrl(miniCard?.image);
      return {
        image: imageUrl ? { fields: { file: { url: imageUrl } } } : undefined,
        url: miniCard?.url || '',
      };
    });
  }, [fields?.miniCards, hasTopLevelPermission, filterByPermissions, getImageUrl]);

  // Process titleInfo
  const titleInfo = useMemo(() => {
    if (!hasTopLevelPermission || !hasPermission(fields?.title)) {
      return { title: '', subtitle: '' };
    }
    return {
      title: fields?.title?.title || '',
      subtitle: fields?.title?.subtitle || '',
      titleFontSize: fields?.title?.titleFontSize || '1x',
      subtitleFontSize: fields?.title?.subtitleFontSize || '1x',
    };
  }, [fields?.title, hasTopLevelPermission, hasPermission]);

  // Process tileInfo
  const tileInfo = useMemo(() => {
    if (!hasTopLevelPermission) {
      return { title: '', subtitle: '' };
    }
    return processTile(fields?.tile);
  }, [fields?.tile, hasTopLevelPermission, processTile]);

  // Process textTileInfo
  const textTileInfo = useMemo(() => {
    if (!hasTopLevelPermission || !hasPermission(fields?.textTile)) {
      return undefined;
    }
    const textTile = fields?.textTile;
    const iconUrl = getImageUrl(textTile?.mainIcon || textTile?.icon);

    const resultsEco = filterByPermissions(textTile?.resultsEco || []).map((res: any) => ({
      icon: getImageUrl(res?.icon),
      leftText: res?.leftText || '',
      rightText: res?.rightText || '',
    }));

    return {
      icon: iconUrl,
      mainText: textTile?.mainText || '',
      ctaText: textTile?.ctaLabel || textTile?.ctaText || '',
      ctaReference: textTile?.ctaReference || '',
      resultsEco,
      tileContent: processTile(textTile?.tile),
    };
  }, [fields?.textTile, hasTopLevelPermission, hasPermission, filterByPermissions, getImageUrl, processTile]);

  if (!hasTopLevelPermission) {
    return null;
  }

  return <EcoSection titleInfo={titleInfo} miniCards={miniCards} tileInfo={tileInfo} textTileInfo={textTileInfo} />;
};

export default EcoSectionTastic;
